import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuthContext } from "@/hooks/useAuthContext";
import SendQueryToChat from "@/components/conversation/utils/SendQueryToChat";
import { useAuth } from "@/hooks/useAuth";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import FeaturePreview from "@/components/FeaturePreview";
import { canUseApp } from "@/utils/appAccess";

type QuestionTypeKey = "general" | "legal" | "verdict";

const questionTypes: Array<{ key: QuestionTypeKey; label: string }> = [
  { key: "general", label: "שאלה כללית" },
  { key: "legal", label: "שאלה לחוק" },
  { key: "verdict", label: "שאלה לפסיקה" },
];

const SUGGESTIONS = [
  // TODO: integrate with LLM
  "ספר לי העושק הצרכני במשפט הישראלי",
  'האם שעות טיסה לחו"ל נחשבות ל"שעות עבודה"?',
  "האם זכות השתיקה חלה גם על מי שהופך מעד לחשוד בביצוע עבירה?",
];

const AssistantPage: React.FC = () => {
  const { user } = useAuthContext();
  const userName = (user?.displayName && user.displayName.trim()) || (user?.email ? user.email.split("@")[0] : "לך");
  const [activeTab, setActiveTab] = useState<QuestionTypeKey>("general");
  const [suggestionQuestion, setSuggestionQuestion] = useState<string | null>(null);
  const auth = useAuth();
  const accountId = auth.getAccountId();

  const { data: account, isSuccess, isLoading, isError } = useGetAccountByIdQuery(
    { accountId: accountId! },
    { skip: !accountId }
  );

  // Show loading state while waiting for accountId or account data
  if (!accountId || isLoading) {
    return (
      <div className="h-full flex flex-col justify-center items-center">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6054FF]"></div>
          <p className="text-[#412D99] text-lg font-medium">מגדיר את החשבון שלך...</p>
        </div>
      </div>
    );
  }

  // Show error state if account fetch failed
  if (isError || !isSuccess) {
    return (
      <div className="h-full flex flex-col justify-center items-center">
        <div className="flex flex-col items-center gap-4 max-w-md text-center px-4">
          <p className="text-red-600 text-lg font-medium">אירעה שגיאה בטעינת פרטי החשבון</p>
          <Button onClick={() => window.location.reload()} className="bg-[#6054FF] text-white">
            נסה שוב
          </Button>
        </div>
      </div>
    );
  }

  if (!canUseApp(account, "ai-assistant")) {
    return <FeaturePreview appId="ai-assistant" />;
  }

  const isExpiredTrial = account?.subscription.status === "expired";

  const handleSuggestionClick = (text: string) => {
    setSuggestionQuestion(text);
  };

  const tabItemClass = (key: QuestionTypeKey) =>
    "relative h-10 md:h-10 flex items-center justify-center transition-colors" +
    (activeTab === key
      ? " text-[#6054FF] border-b border-[#6054FF] drop-shadow-[0_0_8px_rgba(54,65,168,0.2)]"
      : " text-[#9D9FB7]");

  const greetingMessage = `היי ${userName.split(" ")[0]}, איך אוכל לעזור לך היום?`;

  return (
    <section className="flex h-full flex-col items-center justify-center px-2" aria-label="סוכן משפטי">
      {/* Hero blob + title */}
      <div className="flex flex-col items-center gap-6 md:gap-12 select-none">
        <img
          src="/ai-logo.svg"
          alt="AI"
          className="p-3 h-[80px] w-[90px] md:h-[110px] md:w-[120px] pointer-events-none object-contain mix-blend-multiply drop-shadow-lg border-4 border-white rounded-4xl shadow-xl bg-radial-[at_50%_75%] from-white/20 to-white/100"
          draggable={false}
        />

        <h1 className="font-[Rubik] text-center text-[28px] md:text-[44px] leading-[1.2] font-semibold text-[#412D99] px-4">
          {greetingMessage}
        </h1>
      </div>

      {/* Tabs */}
      <div className="mt-4 md:mt-6 flex w-full justify-center">
        <div className="flex h-12 md:h-15 w-full max-w-[444px] items-center justify-center rounded-full px-2 md:px-3 overflow-x-auto">
          {questionTypes.map((type) => (
            <button
              key={type.key}
              className={tabItemClass(type.key) + " flex-shrink-0 min-w-[100px] md:min-w-[140px] text-xs md:text-sm"}
              onClick={() => setActiveTab(type.key)}
              onKeyDown={(e) => e.key === "Enter" && setActiveTab(type.key)}
              aria-label={type.label}
              tabIndex={0}
            >
              <span>{type.label}</span>
            </button>
          ))}
        </div>
      </div>
      <SendQueryToChat suggestionQuestion={suggestionQuestion} isFollowupQuery={false} disable={isExpiredTrial} />

      {/* Suggestions */}
      <div className="mt-4 md:mt-6 flex w-full justify-center px-2">
        <div className="flex w-full max-w-[800px] flex-col items-center gap-2 md:gap-3">
          {SUGGESTIONS.map((text, index) => {
            return (
              <Button
                key={`${index}-${text}`}
                type="button"
                disabled={isExpiredTrial}
                className={
                  "flex h-[40px] md:h-[50px] border-white items-center justify-center rounded-full border bg-white/60 px-4 md:px-5 text-center text-[12px] md:text-[14px] text-[#5B738B] shadow-[0_4.273px_12.819px_-4.273px_rgba(99,140,243,0.2)] w-full max-w-full" +
                  (index === 2 ? " border-white/100 opacity-40" : " border-white") +
                  (isExpiredTrial ? " opacity-50 cursor-not-allowed" : "")
                }
                onClick={() => !isExpiredTrial && handleSuggestionClick(text)}
                onKeyDown={(e) => e.key === "Enter" && !isExpiredTrial && handleSuggestionClick(text)}
                aria-label={text}
                tabIndex={0}
                variant="ghost"
              >
                <span className="truncate">{text}</span>
              </Button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AssistantPage;
